import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Clock, ChevronRight } from 'lucide-react';
import { PeriodicOccurrence, getPeriodicStatus } from '../services/apiService';
import { formatBRL } from '../utils/FormatDecimal';

function formatDate(iso: string): string {
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}

export const PeriodicAlertsWidget: React.FC = () => {
  const [alerts, setAlerts] = useState<PeriodicOccurrence[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const today = new Date();
        const from = new Date(today);
        from.setMonth(from.getMonth() - 1);
        const to = new Date(today);
        to.setMonth(to.getMonth() + 1);
        const res = await getPeriodicStatus({
          from: from.toISOString().slice(0, 10),
          to: to.toISOString().slice(0, 10),
        });
        // Only overdue or pending (alert_active=true)
        const active = res.occurrences.filter(
          (o) => o.status === 'overdue' || (o.status === 'pending' && o.alert_active),
        );
        setAlerts(active);
      } catch (err) {
        console.error('Failed to load periodic alerts', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading || alerts.length === 0) return null;

  const overdueCount = alerts.filter((a) => a.status === 'overdue').length;
  const pendingCount = alerts.filter((a) => a.status === 'pending').length;

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-orange-500" />
          <h3 className="text-sm font-semibold text-gray-700">Contas a vencer</h3>
        </div>
        <Link
          to="/contas-periodicas"
          className="text-xs text-blue-600 hover:underline inline-flex items-center"
        >
          Ver todas <ChevronRight className="h-3 w-3" />
        </Link>
      </div>
      <div className="px-4 py-2 bg-gray-50 border-b border-gray-100 flex gap-4 text-xs">
        {overdueCount > 0 && (
          <span className="text-red-700 font-medium">
            <AlertTriangle className="h-3 w-3 inline mr-1" />
            {overdueCount} vencida{overdueCount > 1 ? 's' : ''}
          </span>
        )}
        {pendingCount > 0 && (
          <span className="text-yellow-700 font-medium">
            <Clock className="h-3 w-3 inline mr-1" />
            {pendingCount} próxima{pendingCount > 1 ? 's' : ''}
          </span>
        )}
      </div>
      <ul className="divide-y divide-gray-100">
        {alerts.slice(0, 8).map((a) => (
          <li
            key={`${a.periodic_id}-${a.due_date}`}
            className={`px-4 py-2 text-sm flex items-center justify-between ${
              a.status === 'overdue' ? 'bg-red-50' : ''
            }`}
          >
            <div>
              <div className="font-medium text-gray-900">{a.description}</div>
              <div className="text-xs text-gray-500">
                {a.status === 'overdue' ? 'Venceu em ' : 'Vence em '}
                {formatDate(a.due_date)} · {a.account}
              </div>
            </div>
            {a.expected_amount && (
              <div className="text-sm font-semibold tabular-nums text-gray-900">
                {formatBRL(parseFloat(a.expected_amount))}
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};
